import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { showSuccess, showError } from "@/utils/toast";
import { Trash2, Download, RefreshCw, Edit2, Trash as TrashIcon, Plus, Zap } from "lucide-react";

interface DateRange {
    id?: number;
    startDate: string;
    endDate: string;
}

interface Country {
    id: number;
    code: string;
    name: string;
}

interface Shop {
    id: number;
    name: string;
    country_id: number;
}

interface Library {
    id: number;
    name: string;
    children?: Library[];
}

interface PdfItem {
    id: number;
    shop_id: number;
    shop_name: string;
    country_id: number;
    country_code: string;
    country_name: string;
    uploaded_by: string;
    uploaded_by_id: number;
    date_ranges: DateRange[];
    pdf_url: string | null;
    file_size_bytes: number;
    processed: boolean;
    images_count: number;
    created_at: string;
    updated_at: string;
}

const PdfList = forwardRef((_, ref) => {
    const [pdfs, setPdfs] = useState<PdfItem[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const [shops, setShops] = useState<Shop[]>([]);
    const [libraries, setLibraries] = useState<Library[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingPdf, setEditingPdf] = useState<PdfItem | null>(null);
    const [editCountryId, setEditCountryId] = useState<number | null>(null);
    const [editShopId, setEditShopId] = useState<number | null>(null);
    const [editDateRanges, setEditDateRanges] = useState<DateRange[]>([]);
    const [editLoading, setEditLoading] = useState(false);
    const [processingPdf, setProcessingPdf] = useState<PdfItem | null>(null);
    const [selectedLibraryId, setSelectedLibraryId] = useState<number | null>(null);
    const [processingLoading, setProcessingLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        refresh: fetchPdfs,
    }));

    useEffect(() => {
        fetchPdfs();
        fetchCountries();
        fetchShops();
        fetchLibraries();
    }, []);

    const fetchPdfs = async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/admin/pdfs");
            if (response.ok) {
                const data = await response.json();
                setPdfs(data.pdfs || []);
            } else {
                showError("Nem sikerült a PDF-ek betöltése.");
            }
        } catch (error) {
            console.error("Hiba a PDF-ek betöltésekor:", error);
            showError("Hiba a PDF-ek betöltésekor.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await fetch("/api/admin/countries");
            if (response.ok) {
                const data = await response.json();
                const formattedCountries = data.countries.map((c: any) => ({
                    id: c.id,
                    code: c.code,
                    name: c.translations?.HU?.name || c.code,
                }));
                setCountries(formattedCountries);
            }
        } catch (error) {
            console.error("Hiba az országok betöltésekor:", error);
        }
    };

    const fetchShops = async () => {
        try {
            const response = await fetch("/api/admin/shops");
            if (response.ok) {
                const data = await response.json();
                setShops(data.shops || []);
            }
        } catch (error) {
            console.error("Hiba a boltok betöltésekor:", error);
        }
    };

    const fetchLibraries = async () => {
        try {
            const response = await fetch("/api/admin/libraries");
            if (response.ok) {
                const data = await response.json();
                setLibraries(data.libraries || []);
            }
        } catch (error) {
            console.error("Hiba a könyvtárak betöltésekor:", error);
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm("Biztosan törölni szeretnéd ezt a PDF-et?")) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/pdfs/${id}`, {
                method: "DELETE",
            });

            if (response.ok) {
                showSuccess("PDF sikeresen törölve!");
                setPdfs(pdfs.filter((pdf) => pdf.id !== id));
            } else {
                const errorData = await response.json();
                showError(errorData.error || "Hiba a PDF törlése során.");
            }
        } catch (error) {
            console.error("Hiba a törlés során:", error);
            showError("Hiba a törlés során.");
        }
    };

    const handleEditOpen = (pdf: PdfItem) => {
        setEditingPdf(pdf);
        setEditCountryId(pdf.country_id);
        setEditShopId(pdf.shop_id);
        setEditDateRanges(pdf.date_ranges || []);
    };

    const handleEditClose = () => {
        setEditingPdf(null);
        setEditCountryId(null);
        setEditShopId(null);
        setEditDateRanges([]);
    };

    const handleProcessOpen = (pdf: PdfItem) => {
        setProcessingPdf(pdf);
        setSelectedLibraryId(null);
    };

    const handleProcessClose = () => {
        setProcessingPdf(null);
        setSelectedLibraryId(null);
    };

    const handleProcessPdf = async () => {
        if (!processingPdf || !selectedLibraryId) {
            showError("Kérjük, válasszon egy könyvtárat!");
            return;
        }

        setProcessingLoading(true);
        try {
            console.log("[DEBUG] PDF feldolgozás indítása - PDF ID:", processingPdf.id, "Library ID:", selectedLibraryId);

            const response = await fetch(`/api/admin/pdfs/${processingPdf.id}/process_pdf`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    library_id: selectedLibraryId,
                }),
            });

            console.log("[DEBUG] Response status:", response.status);
            const responseData = await response.json();
            console.log("[DEBUG] Response data:", responseData);

            if (response.ok) {
                console.log("[DEBUG] PDF feldolgozás sikeresen elindítva");
                showSuccess("PDF feldolgozás elindítva! A képek hamarosan feltöltésre kerülnek.");
                handleProcessClose();
            } else {
                console.error("[DEBUG] Feldolgozás hiba:", responseData);
                showError(responseData.error || "Hiba a feldolgozás indítása során.");
            }
        } catch (error) {
            console.error("[DEBUG] Hiba a feldolgozás során:", error);
            showError("Hiba a feldolgozás indítása során.");
        } finally {
            setProcessingLoading(false);
        }
    };

    const handleAddDateRange = () => {
        const today = new Date().toISOString().split('T')[0];
        setEditDateRanges([
            ...editDateRanges,
            {
                id: Date.now(),
                startDate: today,
                endDate: today,
            },
        ]);
    };

    const handleRemoveDateRange = (id: number | undefined) => {
        setEditDateRanges(editDateRanges.filter((r) => r.id !== id));
    };

    const handleUpdateDateRange = (id: number | undefined, field: 'startDate' | 'endDate', value: string) => {
        setEditDateRanges(
            editDateRanges.map((r) => {
                if (r.id === id) {
                    const updated = { ...r, [field]: value };
                    if (field === 'startDate' && updated.endDate < value) {
                        updated.endDate = value;
                    }
                    if (field === 'endDate' && updated.startDate > value) {
                        updated.startDate = value;
                    }
                    return updated;
                }
                return r;
            })
        );
    };

    const flattenLibraries = (libs: Library[], prefix = ""): Array<{ id: number; name: string; displayName: string }> => {
        return libs.reduce((acc, lib) => {
            const displayName = prefix ? `${prefix} / ${lib.name}` : lib.name;
            acc.push({ id: lib.id, name: lib.name, displayName });
            if (lib.children && lib.children.length > 0) {
                acc.push(...flattenLibraries(lib.children, displayName));
            }
            return acc;
        }, [] as Array<{ id: number; name: string; displayName: string }>);
    };

    const handleSaveEdit = async () => {
        if (!editingPdf || !editCountryId || !editShopId) {
            showError("Kérjük, válasszon ország és bolt!");
            return;
        }

        setEditLoading(true);
        try {
            const response = await fetch(`/api/admin/pdfs/${editingPdf.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    country_id: editCountryId,
                    shop_id: editShopId,
                    date_ranges: editDateRanges,
                }),
            });

            if (response.ok) {
                showSuccess("PDF sikeresen frissítve!");
                handleEditClose();
                fetchPdfs();
            } else {
                const error = await response.json();
                showError(error.message || "Hiba a PDF frissítése során.");
            }
        } catch (error) {
            console.error("Hiba a frissítés során:", error);
            showError("Hiba a frissítés során.");
        } finally {
            setEditLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString("hu-HU", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDateRange = (range: DateRange) => {
        return `${range.startDate} - ${range.endDate}`;
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };

    return (
        <React.Fragment>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle>Feltöltött PDF-ek</CardTitle>
                            <CardDescription>
                                Összesen {pdfs.length} PDF dokumentum
                            </CardDescription>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchPdfs}
                            disabled={loading}
                        >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Frissítés
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-center text-muted-foreground">Betöltés...</p>
                    ) : pdfs.length === 0 ? (
                        <p className="text-center text-muted-foreground">Még nincs feltöltött PDF.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Ország</TableHead>
                                        <TableHead>Bolt</TableHead>
                                        <TableHead>Dátum tartomány</TableHead>
                                        <TableHead>Feltöltő</TableHead>
                                        <TableHead>Fájlméret</TableHead>
                                        <TableHead>Feltöltés ideje</TableHead>
                                        <TableHead className="text-right">Műveletek</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {pdfs.map((pdf) => (
                                        <TableRow key={pdf.id}>
                                            <TableCell>
                                                <span className="font-semibold">{pdf.country_name}</span>
                                                <br />
                                                <span className="text-xs text-muted-foreground">
                                                    ({pdf.country_code})
                                                </span>
                                            </TableCell>
                                            <TableCell>{pdf.shop_name}</TableCell>
                                            <TableCell>
                                                {pdf.date_ranges && pdf.date_ranges.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {pdf.date_ranges.map((range, idx) => (
                                                            <span key={idx} className="block text-sm">
                                                                {formatDateRange(range)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground italic">Nincs megadva</span>
                                                )}
                                            </TableCell>
                                            <TableCell>{pdf.uploaded_by}</TableCell>
                                            <TableCell className="text-sm font-mono text-blue-600">
                                                {formatFileSize(pdf.file_size_bytes)}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {formatDate(pdf.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right space-x-2">
                                                {pdf.pdf_url && (
                                                    <a
                                                        href={pdf.pdf_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center justify-center"
                                                    >
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            title="PDF megnyitása"
                                                        >
                                                            <Download className="h-4 w-4" />
                                                        </Button>
                                                    </a>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleProcessOpen(pdf)}
                                                    disabled={pdf.processed}
                                                    className={`${pdf.processed ? 'text-gray-400 cursor-not-allowed' : 'text-yellow-500 hover:text-yellow-700'}`}
                                                    title={pdf.processed ? `PDF már feldolgozva (${pdf.images_count} kép)` : "PDF feldolgozása"}
                                                >
                                                    <Zap className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleEditOpen(pdf)}
                                                    className="text-blue-500 hover:text-blue-700"
                                                    title="Szerkesztés"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(pdf.id)}
                                                    disabled={pdf.processed}
                                                    className={`${pdf.processed ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'}`}
                                                    title={pdf.processed ? "Nem lehet törölni feldolgozott PDF-et" : "Törlés"}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit Modal */}
            <Dialog open={!!editingPdf} onOpenChange={handleEditClose}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>PDF szerkesztése</DialogTitle>
                        <DialogDescription>
                            {editingPdf?.shop_name} - {editingPdf?.country_name}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Ország választás */}
                        <div className="space-y-2">
                            <Label htmlFor="country-select" className="text-base font-semibold">Ország</Label>
                            <Select value={editCountryId?.toString() || ""} onValueChange={(value) => {
                                setEditCountryId(Number(value));
                                // Reset shop selection when country changes
                                setEditShopId(null);
                            }}>
                                <SelectTrigger id="country-select">
                                    <SelectValue placeholder="Válasszon ország..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {countries.map((country) => (
                                        <SelectItem key={country.id} value={country.id.toString()}>
                                            {country.name} ({country.code})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Bolt választás */}
                        <div className="space-y-2">
                            <Label htmlFor="shop-select" className="text-base font-semibold">Bolt</Label>
                            <Select value={editShopId?.toString() || ""} onValueChange={(value) => setEditShopId(Number(value))}>
                                <SelectTrigger id="shop-select">
                                    <SelectValue placeholder="Válasszon bolt..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {shops
                                        .filter((shop) => editCountryId ? shop.country_id === editCountryId : true)
                                        .map((shop) => (
                                            <SelectItem key={shop.id} value={shop.id.toString()}>
                                                {shop.name}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Dátum tartományok szerkesztése */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-base font-semibold">Akció időtartamai</Label>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddDateRange}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Új dátum hozzáadása
                                </Button>
                            </div>

                            {editDateRanges.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">Nincs megadva dátum tartomány.</p>
                            ) : (
                                <div className="space-y-3">
                                    {editDateRanges.map((range, idx) => (
                                        <div
                                            key={range.id}
                                            className="border rounded-lg p-3 space-y-3 bg-slate-900 dark:bg-slate-800"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold">Dátumtartam #{idx + 1}</span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveDateRange(range.id)}
                                                >
                                                    <TrashIcon className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-2">
                                                    <Label className="text-xs">Kezdés</Label>
                                                    <Input
                                                        type="date"
                                                        value={range.startDate}
                                                        onChange={(e) => handleUpdateDateRange(range.id, "startDate", e.target.value)}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-xs">Vége</Label>
                                                    <Input
                                                        type="date"
                                                        value={range.endDate}
                                                        min={range.startDate}
                                                        onChange={(e) => handleUpdateDateRange(range.id, "endDate", e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleEditClose}>
                            Mégse
                        </Button>
                        <Button onClick={handleSaveEdit} disabled={editLoading}>
                            {editLoading ? "Mentés..." : "Mentés"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Process PDF Modal */}
            <Dialog open={!!processingPdf} onOpenChange={handleProcessClose}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>PDF Feldolgozása</DialogTitle>
                        <DialogDescription>
                            Válassza ki azt a könyvtárat, ahova a képeket fel szeretné tölteni
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="library-select" className="text-base font-semibold">
                                Könyvtár kiválasztása
                            </Label>
                            <Select value={selectedLibraryId?.toString() || ""} onValueChange={(value) => setSelectedLibraryId(Number(value))}>
                                <SelectTrigger id="library-select">
                                    <SelectValue placeholder="Válasszon könyvtárat..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {flattenLibraries(libraries).map((library) => (
                                        <SelectItem key={library.id} value={library.id.toString()}>
                                            {library.displayName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {processingPdf && (
                            <div className="bg-slate-100 dark:bg-slate-900 p-3 rounded text-sm">
                                <p className="font-semibold">{processingPdf.shop_name}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {processingPdf.country_name} ({processingPdf.country_code})
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleProcessClose}>
                            Mégse
                        </Button>
                        <Button onClick={handleProcessPdf} disabled={processingLoading || !selectedLibraryId}>
                            {processingLoading ? "Feldolgozás..." : "Feldolgozás indítása"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );

});

PdfList.displayName = "PdfList";

export default PdfList;
